import React, { createContext, useContext, useState, ReactNode } from 'react';

export type UserRol = 'admin' | 'vendedor' | 'cocinero' | 'fabrica' | 'it' | 'contabilidad';

interface AuthContextType {
    user: string | null;
    token: string | null;
    defaultLocalId: number | null;
    rol: UserRol | null;
    login: (token: string, username: string, defaultLocalId?: number, rol?: UserRol) => void;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<string | null>(() => localStorage.getItem('authUser'));
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('authToken'));
    const [defaultLocalId, setDefaultLocalId] = useState<number | null>(() => {
        const stored = localStorage.getItem('authLocalId');
        return stored ? parseInt(stored, 10) : null;
    });
    const [rol, setRol] = useState<UserRol | null>(() => {
        const stored = localStorage.getItem('authRol');
        return (stored as UserRol) || null;
    });

    const login = (newToken: string, newUser: string, newLocalId?: number, newRol?: UserRol) => {
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('authUser', newUser);
        if (newLocalId) {
            localStorage.setItem('authLocalId', newLocalId.toString());
        }
        const rolToStore = newRol || 'admin';
        localStorage.setItem('authRol', rolToStore);
        setToken(newToken);
        setUser(newUser);
        setDefaultLocalId(newLocalId || null);
        setRol(rolToStore);
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('authLocalId');
        localStorage.removeItem('authRol');
        setToken(null);
        setUser(null);
        setDefaultLocalId(null);
        setRol(null);
    };

    return (
        <AuthContext.Provider value={{ user, token, defaultLocalId, rol, login, logout, isAuthenticated: !!token }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};
